import { useNavigate } from 'react-router-dom';

type DataPoint = {
	label: string;
	value: number;
	color: string;
};

type ChartCardProps = {
	title: string;
	subtitle: string;
	data: DataPoint[];
};

const palette = {
	blue: '#67d7ec',
	brown: '#8b7b24',
	green: '#a7df3a',
	gray: '#b9b9b9',
	orange: '#ff7a59',
	purple: '#a97dff',
	red: '#ff3d7f',
	white: '#f2f2f2',
	yellow: '#f0db3f',
	black: '#262626',
};

const favoriteMen: DataPoint[] = [
	{ label: 'Blue', value: 57, color: palette.blue },
	{ label: 'Brown', value: 2, color: palette.brown },
	{ label: 'Green', value: 14, color: palette.green },
	{ label: 'Grey', value: 3, color: palette.gray },
	{ label: 'Orange', value: 5, color: palette.orange },
	{ label: 'Red', value: 7, color: palette.red },
	{ label: 'White', value: 2, color: palette.white },
	{ label: 'Yellow', value: 1, color: palette.yellow },
	{ label: 'Black', value: 9, color: palette.black },
];

const favoriteWomen: DataPoint[] = [
	{ label: 'Blue', value: 35, color: palette.blue },
	{ label: 'Brown', value: 3, color: palette.brown },
	{ label: 'Green', value: 14, color: palette.green },
	{ label: 'Grey', value: 1, color: palette.gray },
	{ label: 'Orange', value: 5, color: palette.orange },
	{ label: 'Purple', value: 23, color: palette.purple },
	{ label: 'Red', value: 9, color: palette.red },
	{ label: 'White', value: 1, color: palette.white },
	{ label: 'Yellow', value: 3, color: palette.yellow },
	{ label: 'Black', value: 6, color: palette.black },
];

const leastMen: DataPoint[] = [
	{ label: 'Blue', value: 1, color: palette.blue },
	{ label: 'Brown', value: 27, color: palette.brown },
	{ label: 'Green', value: 2, color: palette.green },
	{ label: 'Grey', value: 5, color: palette.gray },
	{ label: 'Orange', value: 22, color: palette.orange },
	{ label: 'Purple', value: 22, color: palette.purple },
	{ label: 'Red', value: 2, color: palette.red },
	{ label: 'White', value: 5, color: palette.white },
	{ label: 'Yellow', value: 13, color: palette.yellow },
	{ label: 'Black', value: 1, color: palette.black },
];

const leastWomen: DataPoint[] = [
	{ label: 'Brown', value: 20, color: palette.brown },
	{ label: 'Green', value: 6, color: palette.green },
	{ label: 'Grey', value: 17, color: palette.gray },
	{ label: 'Orange', value: 33, color: palette.orange },
	{ label: 'Purple', value: 8, color: palette.purple },
	{ label: 'White', value: 3, color: palette.white },
	{ label: 'Yellow', value: 13, color: palette.yellow },
];

const formatGradient = (data: DataPoint[]) => {
	const total = data.reduce((sum, item) => sum + item.value, 0);
	let current = 0;

	const segments = data.map((item) => {
		const start = (current / total) * 100;
		current += item.value;
		const end = (current / total) * 100;
		return `${item.color} ${start}% ${end}%`;
	});

	return `conic-gradient(${segments.join(', ')})`;
};

const ChartCard = ({ title, subtitle, data }: ChartCardProps) => (
	<article
		style={{
			padding: 'clamp(18px, 4vw, 24px)',
			borderRadius: '22px',
			background: 'rgba(255, 255, 255, 0.06)',
			boxShadow: '0 18px 50px rgba(0, 0, 0, 0.24)',
			border: '1px solid rgba(255, 255, 255, 0.08)',
			display: 'flex',
			flexDirection: 'column',
			gap: '18px',
		}}
	>
		<div>
			<h3 style={{ margin: 0, fontSize: '1.2rem', color: '#ffffff' }}>{title}</h3>
			<p style={{ margin: '6px 0 0', color: 'rgba(233, 238, 255, 0.72)', fontSize: '0.95rem' }}>{subtitle}</p>
		</div>

		<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '18px', flexWrap: 'wrap', flexDirection: 'column' }}>
			<div
				style={{
					position: 'relative',
					width: 'min(220px, 72vw)',
					height: 'min(220px, 72vw)',
					borderRadius: '50%',
					background: formatGradient(data),
					flexShrink: 0,
				}}
			>
				<div
					style={{
						position: 'absolute',
						inset: '22%',
						borderRadius: '50%',
						background: '#ffffff',
						boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.04)',
					}}
				/>
			</div>

			<ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px', width: '100%', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
				{data.map((item) => (
					<li key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'rgba(241, 244, 255, 0.88)', fontSize: '0.92rem', minWidth: 0 }}>
						<span
							style={{
								width: '12px',
								height: '12px',
								borderRadius: '999px',
								background: item.color,
								boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.08)',
							}}
						/>
						<span>{item.label} {item.value}%</span>
					</li>
				))}
			</ul>
		</div>
	</article>
);

const Step3 = () => {
	const navigate = useNavigate();
	return (
	<main
		style={{
			minHeight: '100vh',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			padding: 'clamp(14px, 4vw, 24px)',
			background:
				'radial-gradient(circle at top left, rgba(5, 217, 232, 0.16), transparent 30%), radial-gradient(circle at bottom right, rgba(255, 107, 107, 0.14), transparent 24%), linear-gradient(180deg, #07060d 0%, #12101d 100%)',
			color: '#ffffff',
		}}
	>
			<section style={{ width: 'min(100%, 1200px)', padding: 'clamp(18px, 4vw, 32px)', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.12)', background: 'rgba(13, 12, 24, 0.92)', boxShadow: '0 24px 80px rgba(0, 0, 0, 0.45)', display: 'flex', flexDirection: 'column', gap: 'clamp(20px, 4vw, 32px)' }}>
			<header style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
				<p style={{ margin: 0, color: '#6ef6ff', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.85rem' }}>
					Step 3
				</p>
				<h1 style={{ margin: 0, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.05, color: '#ffffff' }}>
					Male and female favorite colors
				</h1>
				<p style={{ margin: 0, maxWidth: '70ch', color: 'rgba(233, 238, 255, 0.78)', lineHeight: 1.6 }}>
					This page shows the favorite and least favorite color data as readable charts, matching the structure from the reference image.
				</p>
			</header>

			<div style={{ display: 'grid', gap: '24px' }}>
				<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
					<ChartCard title="Male" subtitle="Favorite colors" data={favoriteMen} />
					<ChartCard title="Female" subtitle="Favorite colors" data={favoriteWomen} />
				</div>

				<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
					<ChartCard title="Male" subtitle="Least favorite colors" data={leastMen} />
					<ChartCard title="Female" subtitle="Least favorite colors" data={leastWomen} />
				</div>
			</div>

			<footer style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
				<button
					type="button"
					onClick={() => navigate('/step2')}
					style={{
						minHeight: '48px',
						padding: '12px 20px',
						borderRadius: '12px',
						border: 'none',
						background: 'linear-gradient(135deg, #05d9e8 0%, #39ff14 100%)',
						color: '#071018',
						fontWeight: 800,
						cursor: 'pointer',
						boxShadow: '0 12px 30px rgba(5, 217, 232, 0.18)',
						textTransform: 'uppercase',
					}}
				>
					Back to Step 2
				</button>
			</footer>
		</section>
	</main>
	);
};

export default Step3;

